import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { fetchScorerView, postScorerSubmission, updateScorerDraft } from '../../../Api/Advise';
import { ScorerAnswers, ScorerConfirmation, ScorerOpenView, ScorerView } from '../../../types/advise';
import { refusalMessage } from '../../../utils/refusalMessage';
import { clearScorerToken, readScorerToken } from '../../../utils/scorerToken';
import { usePendingSaves } from './usePendingSaves';

// Saving only once the scorer pauses keeps a link under the API's 30 requests a minute, opens included, unless they
// pause every 2 seconds for a whole minute.
const DRAFT_SAVE_MS = 2000;

// The API counts a link's requests in fixed one-minute windows, and a browser can't read its Retry-After.
const RATE_LIMIT_WAIT_MS = 60_000;

const SEND_FAILED = 'Your scores could not be sent. Please try again.';

const RATE_LIMITED = 'You sent a lot of changes in the last minute. Please wait a minute, then submit again.';

export type ScorerProblem = 'invalid' | 'expired' | 'superseded' | 'revoked' | 'failed';

const PROBLEMS: Record<string, ScorerProblem> = {
  invalid_link: 'invalid',
  link_expired: 'expired',
  link_superseded: 'superseded',
  link_revoked: 'revoked'
};

type ScorerPageState =
  | { kind: 'loading' }
  | { kind: 'open'; view: ScorerOpenView }
  | { kind: 'completed'; confirmation: ScorerConfirmation }
  | { kind: 'problem'; problem: ScorerProblem };

type ScorerError = { message: string; errorResponse?: AxiosError };

const statusOf = (error: ScorerError) => error.errorResponse?.response?.status;

const bodyOf = (error: ScorerError) => error.errorResponse?.response?.data as { error?: string; message?: string };

// 401 and 410 are the link's own answers: it is unknown, or closed for good.
const linkRefused = (error: ScorerError) => [401, 410].includes(statusOf(error) ?? 0);

// Unrated functions are left out, so they reach the API as unrated and never as a number.
const answersOf = (ratings: Map<string, number>, comment: string): ScorerAnswers => ({
  scores: Object.fromEntries(ratings),
  comment: comment.trim() ? comment : null
});

/** The scorer page's one request: loading it, saving its draft as the scorer goes, and the final submit. */
export const useScorer = () => {
  const [state, setState] = useState<ScorerPageState>({ kind: 'loading' });
  const [ratings, setRatings] = useState<Map<string, number>>(new Map());
  const [comment, setComment] = useState('');
  const [saveFailed, setSaveFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitRefusal, setSubmitRefusal] = useState<string | null>(null);
  // A slider fires faster than renders, so each save reads the newest answers from here.
  const answers = useRef({ ratings, comment });
  const rateLimitRetry = useRef<NodeJS.Timeout | undefined>(undefined);

  const showProblem = useCallback((error: ScorerError) => {
    if (linkRefused(error)) clearScorerToken();
    setState({ kind: 'problem', problem: PROBLEMS[bodyOf(error)?.error ?? ''] ?? 'failed' });
  }, []);

  const show = useCallback((view: ScorerView) => {
    if (view.state === 'completed') {
      setState({ kind: 'completed', confirmation: view });
      return;
    }
    const restored = { ratings: new Map(Object.entries(view.draft.scores)), comment: view.draft.comment ?? '' };
    answers.current = restored;
    setRatings(restored.ratings);
    setComment(restored.comment);
    setState({ kind: 'open', view });
  }, []);

  const load = useCallback(async () => {
    const token = readScorerToken();
    if (!token) {
      setState({ kind: 'problem', problem: 'invalid' });
      return;
    }
    setState({ kind: 'loading' });
    const result = await fetchScorerView(token);
    if (result.error) showProblem(result.error);
    else show(result.data);
  }, [show, showProblem]);

  useEffect(() => {
    load();
  }, [load]);

  const { debouncedSave, flushSaves, discardSaves } = usePendingSaves(
    state.kind === 'open',
    () => setSaveFailed(true),
    DRAFT_SAVE_MS
  );

  // Once the form is gone a failed save has nowhere to go, and closing the page must not ask to stay or send it.
  useEffect(() => {
    if (state.kind !== 'completed' && state.kind !== 'problem') return;
    clearTimeout(rateLimitRetry.current);
    discardSaves();
  }, [state.kind, discardSaves]);

  useEffect(() => () => clearTimeout(rateLimitRetry.current), []);

  const saveDraft = () =>
    debouncedSave('draft', async () => {
      const token = readScorerToken();
      if (!token) return { error: null };
      const result = await updateScorerDraft(token, answersOf(answers.current.ratings, answers.current.comment));
      if (result.error) {
        // A closed link is shown, not retried.
        if (linkRefused(result.error)) {
          showProblem(result.error);
          return { error: null };
        }
        if (statusOf(result.error) === 429) {
          clearTimeout(rateLimitRetry.current);
          rateLimitRetry.current = setTimeout(saveDraft, RATE_LIMIT_WAIT_MS);
        }
        return result;
      }
      setSaveFailed(false);
      if (result.data.state === 'completed') setState({ kind: 'completed', confirmation: result.data });
      return result;
    });

  const rate = (functionId: string, rating: number) => {
    const next = new Map(answers.current.ratings).set(functionId, rating);
    answers.current = { ...answers.current, ratings: next };
    setRatings(next);
    saveDraft();
  };

  const changeComment = (text: string) => {
    answers.current = { ...answers.current, comment: text };
    setComment(text);
    saveDraft();
  };

  const submit = async () => {
    const token = readScorerToken();
    if (!token) {
      setState({ kind: 'problem', problem: 'invalid' });
      return;
    }
    setSubmitting(true);
    setSubmitRefusal(null);
    await flushSaves();
    const result = await postScorerSubmission(token, answersOf(answers.current.ratings, answers.current.comment));
    setSubmitting(false);
    if (!result.error) {
      setState({ kind: 'completed', confirmation: result.data });
      return;
    }
    // Already submitted, from another tab or device: the link now answers the confirmation.
    if (statusOf(result.error) === 409) await load();
    else if (linkRefused(result.error)) showProblem(result.error);
    else if (statusOf(result.error) === 429) setSubmitRefusal(RATE_LIMITED);
    else setSubmitRefusal(refusalMessage(result.error, SEND_FAILED, SEND_FAILED));
  };

  return { state, ratings, comment, rate, changeComment, saveFailed, submit, submitting, submitRefusal, retry: load };
};
